import { useConfigurationStore } from '@stores/configurationStore'
import { useJackpotStore } from '@stores/jackpotStore'
import { SlotContent } from '@utils/Slot'
import React, { useEffect, useRef, useState } from 'react'
import JackpotStatisticsItem from '../common/JackpotStatisticsItem'
import { jackpotCardsConfig } from './config'
import JackpotCardsTooltip from './JackpotCardsTooltip'
import { getAmountAfterHideCents } from '@utils/money'
import {
    JackpotSuits,
    JackpotSuitRomanNumberMap,
    JackpotSuitIndexMap,
    JackpotPhase,
    MAX_CARDS_OF_SUIT,
} from 'game-ui-types'
import { withAutoBlur } from '@utils/misc'

interface JackpotCardsStatisticsItemProps {
    type: JackpotSuits
    isTooltipActive: boolean
    onClick: (suit: JackpotSuits) => void
    onMouseOver?: (suit: JackpotSuits) => void
    onMouseOut?: () => void
}

/**
 * JackpotCardsStatisticsItem Component
 *
 * Renders jackpot card tooltip, small suits and passes stats data to generic jackpot statistics item.
 * Keep track and dictates/controls how to animate jackpot value for Jackpot Cards.
 * Values are kept in cents and then Odometer will take care of formatting
 */
const JackpotCardsStatisticsItem: React.FC<JackpotCardsStatisticsItemProps> = ({
    type,
    isTooltipActive,
    onClick,
    onMouseOver,
    onMouseOut,
}) => {
    const playerName = useConfigurationStore((state) => state.playerName)
    const viewport = useConfigurationStore((state) => state.viewport)
    const gameType = useConfigurationStore((state) => state.gameType)
    const viewportConfig = jackpotCardsConfig(gameType)[type][viewport]

    const jackpotCardsStatistics = useJackpotStore((state) => state.jackpotCardsStatistics)
    const pickedSuits = useJackpotStore((state) => state.pickedSuits)
    const jackpotPhase = useJackpotStore((state) => state.jackpotPhase)
    const isJackpotRoundRunning = useJackpotStore((state) => state.isJackpotRoundRunning)
    const freezeIndex = useJackpotStore((state) => state.freezeIndex)
    const hideCents = useJackpotStore((state) => state.currencyHideCentsOnlyInJackpotBoxes)
    const odometerMaxLimit = useJackpotStore((state) => state.jackpotStatsMaxOdometerLimit)

    const [amount, setAmount] = useState(-1)
    const realTimeAmountInOdometerRef = useRef(amount)
    const previousAmount = useRef(amount)
    const [isAmountStatic, setIsAmountStatic] = useState(false)
    const [shouldUseOdometer, setShouldUseOdometer] = useState(true)
    const [winningUser, setWinningUser] = useState('')
    const winningUserDate = useRef('')
    const isWinnerBlinking = useRef(false)

    const getWinningUserDateFromStats = () => {
        return jackpotCardsStatistics[`lastWinDateLevel${JackpotSuitRomanNumberMap[type]}`]
    }

    const getWinningUserFromStats = () => {
        return jackpotCardsStatistics[`lastWinUserLevel${JackpotSuitRomanNumberMap[type]}`]
    }

    const getWinningAmountFromStats = () => {
        return jackpotCardsStatistics[`lastWinLevel${JackpotSuitRomanNumberMap[type]}`]
    }

    const getJackpotAmountFromStats = () => {
        return jackpotCardsStatistics[`level${JackpotSuitRomanNumberMap[type]}`]
    }

    const handleWinningUserPresentationComplete = () => {
        isWinnerBlinking.current = false

        if (winningUser !== '') setWinningUser('')
    }

    const handleBlinkingComplete = () => {
        isWinnerBlinking.current = false

        if (freezeIndex !== JackpotSuitIndexMap[type]) {
            setIsAmountStatic(true)
            setShouldUseOdometer(false)
            setAmount(getJackpotAmountFromStats() || 0)
        }
    }

    const handleRealTimeOdometerUpdate = (currentValueInOdometer: number) => {
        // Keep track of what is shown in the panel (since we animate the current value towards target value)
        // This value is always in cents even if hideCents is true
        realTimeAmountInOdometerRef.current = currentValueInOdometer
    }

    useEffect(() => {
        // If no jackpot values exit
        if (getJackpotAmountFromStats() < 0) return

        // If last card is drawn but jackpot is still in running phase
        if (freezeIndex >= 0 && jackpotPhase === JackpotPhase.RUNNING) {
            setIsAmountStatic(true)
            setShouldUseOdometer(false)

            return
        }

        if (winningUserDate.current === '') winningUserDate.current = getWinningUserDateFromStats()

        // Keep track of previous amount to understand next steps
        previousAmount.current = amount

        const isFrozen = freezeIndex === JackpotSuitIndexMap[type]

        // If we haven't parsed current date and the winner is not the current player - otherwise they will win jackpot and the date is reset to latest there
        const hasANewPlayerWonJackpot =
            getWinningUserDateFromStats() !== winningUserDate.current && playerName !== getWinningUserFromStats()

        // If player finished jackpot and has won
        if (isFrozen && (jackpotPhase === JackpotPhase.OUTRO_STARTED || jackpotPhase === JackpotPhase.OUTRO_CLICKED)) {
            if (amount !== 0) {
                setAmount(0)
                setShouldUseOdometer(false)
            }

            // If showing winning user presentation while current player has won, remove presentation to show countdown to 0
            if (winningUser !== '') setWinningUser('')

            winningUserDate.current = getWinningUserDateFromStats()

            // If win animation clicked, skip countdown animation and show 0
            setIsAmountStatic(jackpotPhase === JackpotPhase.OUTRO_CLICKED)
        } else if (!isFrozen && hasANewPlayerWonJackpot) {
            // If a Player has won a jackpot but the panel is not frozen, start blinking animation with winning amount
            winningUserDate.current = getWinningUserDateFromStats()
            setWinningUser(getWinningUserFromStats())
            isWinnerBlinking.current = true

            setAmount(getWinningAmountFromStats())
            setIsAmountStatic(true)
        } else if (!winningUser) {
            // Otherwise default to setting a new target and check animation type
            if (amount !== getJackpotAmountFromStats()) {
                const newAmount = getJackpotAmountFromStats() ?? 0

                setAmount(newAmount)

                const targetAmount = getAmountAfterHideCents(newAmount, hideCents)
                const realTimeAmount = getAmountAfterHideCents(realTimeAmountInOdometerRef.current, hideCents)

                setShouldUseOdometer(Math.abs(targetAmount - realTimeAmount) <= odometerMaxLimit[type])
                // If previous update amount was 0, set the latest value statically, otherwise animate
                setIsAmountStatic(previousAmount.current === 0)
            }
        }
    }, [jackpotCardsStatistics, freezeIndex, jackpotPhase])

    const generateSmallSuits = () => {
        const elements = []

        for (let i = 0; i < MAX_CARDS_OF_SUIT; i++) {
            const dimensions = viewportConfig.extended.smallSuits.dimensions
            const position = viewportConfig.extended.smallSuits.positions[i]

            elements.push(
                <div
                    id={`small-suits-${type.toLowerCase()}`}
                    className={`${type.toLowerCase()}-small-suits fixed`}
                    style={{ top: position.y, left: position.x, width: dimensions, height: dimensions }}
                >
                    <img
                        src={
                            i < pickedSuits[type]
                                ? viewportConfig.extended.smallSuits.images.fullSmallSuit
                                : viewportConfig.extended.smallSuits.images.emptySmallSuit
                        }
                    />
                </div>
            )
        }

        return elements
    }

    return (
        <JackpotStatisticsItem
            config={jackpotCardsConfig(gameType)[type]}
            type={type}
            realTimeAmount={realTimeAmountInOdometerRef.current > -1 ? realTimeAmountInOdometerRef.current : undefined}
            amount={amount}
            isAmountStatic={isAmountStatic}
            shouldUseOdometer={shouldUseOdometer}
            winningUser={winningUser}
            onWinningUserPresentationComplete={handleWinningUserPresentationComplete}
            onBlinkingComplete={handleBlinkingComplete}
            onClick={withAutoBlur(() => onClick(type))}
            onMouseOver={() => onMouseOver?.(type)}
            onMouseOut={onMouseOut}
            onRealTimeOdometerUpdate={handleRealTimeOdometerUpdate}
        >
            <SlotContent slot="above">
                {isJackpotRoundRunning && <div className="small-suits absolute">{generateSmallSuits()}</div>}
                <JackpotCardsTooltip suit={type} isActive={isTooltipActive} />
            </SlotContent>
        </JackpotStatisticsItem>
    )
}

export default JackpotCardsStatisticsItem
